import { type FC, useEffect, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import DocumentBrandingSettingsCard from '@/components/feature/settings/DocumentBrandingSettingsCard';
import { LoadingState, useToast } from '@/components/base';
import {
  createEmptyOfficeSettingsForm,
  type OfficeSettingsFormState,
  useOfficeSettings,
} from '@/hooks/useOfficeSettings';

type FieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  rows?: number;
  helper?: string;
  inputMode?: 'search' | 'email' | 'none' | 'tel' | 'text' | 'url' | 'decimal' | 'numeric';
  autoComplete?: string;
};

const Field: FC<FieldProps> = ({
  label,
  value,
  onChange,
  placeholder = '',
  type = 'text',
  rows = 3,
  helper,
  inputMode,
  autoComplete,
}) => (
  <label className="space-y-1.5">
    <span className="block text-xs font-medium text-foreground-600">{label}</span>
    {type === 'textarea' ? (
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 resize-none focus:outline-none focus:border-primary-300"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        autoComplete={autoComplete}
        className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
      />
    )}
    {helper && <p className="text-[11px] text-foreground-500">{helper}</p>}
  </label>
);

const preferenceCurrencyOptions = ['BRL', 'USD', 'EUR'];
const preferenceLanguageOptions = ['pt-BR', 'en-US', 'es-ES'];
const preferenceTimezoneOptions = ['America/Sao_Paulo', 'UTC', 'America/New_York', 'Europe/Lisbon'];
const preferenceDateFormatOptions = ['dd/MM/yyyy', 'yyyy-MM-dd', 'MM/dd/yyyy'];
const bankAccountTypeOptions = ['corrente', 'poupanca', 'pagamento', 'outro'];
const pixKeyTypeOptions = ['cpf', 'cnpj', 'email', 'phone', 'random'];
const brazilStates = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

const SettingsPage: FC = () => {
  const { addToast } = useToast();
  const {
    officeSettings,
    isLoadingOfficeSettings,
    isSavingOfficeSettings,
    officeSettingsError,
    officeSettingsSuccess,
    loadOfficeSettings,
    saveOfficeSettings,
  } = useOfficeSettings();
  const [form, setForm] = useState<OfficeSettingsFormState>(createEmptyOfficeSettingsForm());
  const [hasLoadedForm, setHasLoadedForm] = useState(false);

  useEffect(() => {
    if (officeSettings) {
      setForm(officeSettings);
      setHasLoadedForm(true);
      return;
    }

    if (!isLoadingOfficeSettings && !hasLoadedForm) {
      setForm(createEmptyOfficeSettingsForm());
      setHasLoadedForm(true);
    }
  }, [hasLoadedForm, isLoadingOfficeSettings, officeSettings]);

  const handleSave = async () => {
    if (!form.officeName.trim()) {
      addToast('O nome do escritório é obrigatório.', 'warning');
      return;
    }

    try {
      await saveOfficeSettings(form);
      addToast('Configurações do escritório salvas com sucesso.', 'success');
    } catch (error: unknown) {
      addToast(error instanceof Error ? error.message : 'Não foi possível salvar as configurações do escritório.', 'error');
    }
  };

  const sections = [
    {
      title: 'Identificação do escritório',
      description: 'Dados institucionais que podem aparecer em documentos e comunicações.',
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Nome do escritório *" value={form.officeName} onChange={(value) => setForm((previous) => ({ ...previous, officeName: value }))} placeholder="Nome do escritório" />
          <Field label="Razão social" value={form.legalName} onChange={(value) => setForm((previous) => ({ ...previous, legalName: value }))} placeholder="Razão social" />
          <Field label="CNPJ / documento" value={form.documentNumber} onChange={(value) => setForm((previous) => ({ ...previous, documentNumber: value }))} placeholder="00.000.000/0000-00" />
          <Field label="Site" value={form.website} onChange={(value) => setForm((previous) => ({ ...previous, website: value }))} placeholder="https://..." />
          <Field label="E-mail" value={form.email} onChange={(value) => setForm((previous) => ({ ...previous, email: value }))} placeholder="contato@..." />
          <Field label="Telefone" value={form.phone} onChange={(value) => setForm((previous) => ({ ...previous, phone: value }))} placeholder="(00) 0000-0000" />
          <Field label="WhatsApp" value={form.whatsapp} onChange={(value) => setForm((previous) => ({ ...previous, whatsapp: value }))} placeholder="(00) 00000-0000" />
          <Field label="Instagram" value={form.instagram} onChange={(value) => setForm((previous) => ({ ...previous, instagram: value }))} placeholder="@perfil" />
        </div>
      ),
    },
    {
      title: 'Endereço',
      description: 'Endereço principal utilizado em cadastros e documentos.',
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Logradouro" value={form.addressLine} onChange={(value) => setForm((previous) => ({ ...previous, addressLine: value }))} placeholder="Rua, avenida, praça..." />
          <Field label="Número" value={form.addressNumber} onChange={(value) => setForm((previous) => ({ ...previous, addressNumber: value }))} placeholder="123" />
          <Field label="Complemento" value={form.addressComplement} onChange={(value) => setForm((previous) => ({ ...previous, addressComplement: value }))} placeholder="Sala, bloco, andar..." />
          <Field label="Bairro" value={form.district} onChange={(value) => setForm((previous) => ({ ...previous, district: value }))} placeholder="Bairro" />
          <Field label="Cidade" value={form.city} onChange={(value) => setForm((previous) => ({ ...previous, city: value }))} placeholder="Cidade" />
          <label className="space-y-1.5">
            <span className="block text-xs font-medium text-foreground-600">Estado</span>
            <select
              value={form.state}
              onChange={(event) => setForm((previous) => ({ ...previous, state: event.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
            >
              <option value="">Selecione</option>
              {brazilStates.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </label>
          <Field label="CEP" value={form.zipCode} onChange={(value) => setForm((previous) => ({ ...previous, zipCode: value }))} placeholder="00000-000" />
          <Field label="País" value={form.country} onChange={(value) => setForm((previous) => ({ ...previous, country: value }))} placeholder="Brasil" />
        </div>
      ),
    },
    {
      title: 'Advogado responsável',
      description: 'Dados do profissional principal vinculado ao escritório.',
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Nome do advogado" value={form.lawyerName} onChange={(value) => setForm((previous) => ({ ...previous, lawyerName: value }))} placeholder="Nome completo" />
          <Field label="Documento do advogado" value={form.lawyerDocument} onChange={(value) => setForm((previous) => ({ ...previous, lawyerDocument: value }))} placeholder="CPF ou outro documento" />
          <Field label="OAB" value={form.lawyerOab} onChange={(value) => setForm((previous) => ({ ...previous, lawyerOab: value }))} placeholder="123456" />
          <Field label="Seccional da OAB" value={form.lawyerOabState} onChange={(value) => setForm((previous) => ({ ...previous, lawyerOabState: value }))} placeholder="SP" />
          <Field label="E-mail profissional" value={form.lawyerEmail} onChange={(value) => setForm((previous) => ({ ...previous, lawyerEmail: value }))} placeholder="advogado@..." />
          <Field label="Telefone profissional" value={form.lawyerPhone} onChange={(value) => setForm((previous) => ({ ...previous, lawyerPhone: value }))} placeholder="(00) 0000-0000" />
          <Field label="Cargo / função" value={form.lawyerRole} onChange={(value) => setForm((previous) => ({ ...previous, lawyerRole: value }))} placeholder="Advogado responsável" />
        </div>
      ),
    },
    {
      title: 'Dados bancários',
      description: 'Dados de recebimento para propostas, contratos e pagamentos.',
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Banco" value={form.bankName} onChange={(value) => setForm((previous) => ({ ...previous, bankName: value }))} placeholder="Nome do banco" />
          <Field label="Agência" value={form.bankAgency} onChange={(value) => setForm((previous) => ({ ...previous, bankAgency: value }))} placeholder="0000" />
          <Field label="Conta" value={form.bankAccount} onChange={(value) => setForm((previous) => ({ ...previous, bankAccount: value }))} placeholder="000000-0" />
          <label className="space-y-1.5">
            <span className="block text-xs font-medium text-foreground-600">Tipo de conta</span>
            <select
              value={form.bankAccountType}
              onChange={(event) => setForm((previous) => ({ ...previous, bankAccountType: event.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
            >
              <option value="">Selecione</option>
              {bankAccountTypeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <Field label="Chave PIX" value={form.pixKey} onChange={(value) => setForm((previous) => ({ ...previous, pixKey: value }))} placeholder="Chave PIX" />
          <label className="space-y-1.5">
            <span className="block text-xs font-medium text-foreground-600">Tipo da chave PIX</span>
            <select
              value={form.pixKeyType}
              onChange={(event) => setForm((previous) => ({ ...previous, pixKeyType: event.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
            >
              <option value="">Selecione</option>
              {pixKeyTypeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <Field label="Favorecido" value={form.paymentHolderName} onChange={(value) => setForm((previous) => ({ ...previous, paymentHolderName: value }))} placeholder="Nome do titular" />
          <Field label="Documento do favorecido" value={form.paymentHolderDocument} onChange={(value) => setForm((previous) => ({ ...previous, paymentHolderDocument: value }))} placeholder="CPF/CNPJ do titular" />
        </div>
      ),
    },
    {
      title: 'Dados para documentos',
      description: 'Textos usados em documentos, propostas, contratos e peças.',
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Cidade padrão" value={form.defaultCity} onChange={(value) => setForm((previous) => ({ ...previous, defaultCity: value }))} placeholder="Cidade" />
          <Field label="Estado padrão" value={form.defaultState} onChange={(value) => setForm((previous) => ({ ...previous, defaultState: value }))} placeholder="UF" />
          <Field label="Foro padrão" value={form.defaultForum} onChange={(value) => setForm((previous) => ({ ...previous, defaultForum: value }))} placeholder="Foro competente" />
          <Field
            label="Rodapé padrão"
            value={form.documentFooter}
            onChange={(value) => setForm((previous) => ({ ...previous, documentFooter: value }))}
            type="textarea"
            rows={4}
            placeholder="Texto para rodapé de documentos"
          />
          <Field
            label="Texto de assinatura"
            value={form.signatureText}
            onChange={(value) => setForm((previous) => ({ ...previous, signatureText: value }))}
            type="textarea"
            rows={4}
            placeholder="Texto de assinatura"
          />
          <Field
            label="Observações contratuais"
            value={form.contractObservations}
            onChange={(value) => setForm((previous) => ({ ...previous, contractObservations: value }))}
            type="textarea"
            rows={4}
            placeholder="Observações para contratos"
          />
          <Field
            label="Observações de procuração"
            value={form.powerOfAttorneyObservations}
            onChange={(value) => setForm((previous) => ({ ...previous, powerOfAttorneyObservations: value }))}
            type="textarea"
            rows={4}
            placeholder="Observações para procurações"
          />
        </div>
      ),
    },
    {
      title: 'Preferências',
      description: 'Preferências gerais de exibição e padronização do sistema.',
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="space-y-1.5">
            <span className="block text-xs font-medium text-foreground-600">Moeda padrão</span>
            <select
              value={form.defaultCurrency}
              onChange={(event) => setForm((previous) => ({ ...previous, defaultCurrency: event.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
            >
              <option value="">Selecione</option>
              {preferenceCurrencyOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1.5">
            <span className="block text-xs font-medium text-foreground-600">Idioma padrão</span>
            <select
              value={form.defaultLanguage}
              onChange={(event) => setForm((previous) => ({ ...previous, defaultLanguage: event.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
            >
              <option value="">Selecione</option>
              {preferenceLanguageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1.5">
            <span className="block text-xs font-medium text-foreground-600">Fuso horário</span>
            <select
              value={form.timezone}
              onChange={(event) => setForm((previous) => ({ ...previous, timezone: event.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
            >
              <option value="">Selecione</option>
              {preferenceTimezoneOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1.5">
            <span className="block text-xs font-medium text-foreground-600">Formato de data</span>
            <select
              value={form.dateFormat}
              onChange={(event) => setForm((previous) => ({ ...previous, dateFormat: event.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
            >
              <option value="">Selecione</option>
              {preferenceDateFormatOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        </div>
      ),
    },
  ];

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">
        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-bold text-foreground-900">Configurações</h2>
          <p className="text-sm text-foreground-500">Personalize os dados institucionais usados pelo sistema.</p>
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Essas informações poderão ser usadas em documentos, contratos, propostas, mensagens e relatórios do sistema.
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void loadOfficeSettings()}
              disabled={isLoadingOfficeSettings || isSavingOfficeSettings}
              className="px-3 py-2 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <i className="ri-refresh-line mr-1"></i> Recarregar
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={isSavingOfficeSettings}
              className="px-3 py-2 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSavingOfficeSettings ? 'Salvando...' : 'Salvar configurações'}
            </button>
          </div>

          <div className="text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-50 px-2 py-1">
              <i className="ri-lock-line"></i> Configuração única por instalação
            </span>
          </div>
        </div>

        {officeSettingsError && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-start gap-2">
            <i className="ri-alert-line mt-0.5"></i>
            <div className="flex-1">
              <p className="font-medium">Não foi possível carregar ou salvar as configurações.</p>
              <p className="text-red-700 mt-0.5">{officeSettingsError}</p>
            </div>
          </div>
        )}

        {officeSettingsSuccess && !officeSettingsError && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 flex items-start gap-2">
            <i className="ri-checkbox-circle-line mt-0.5"></i>
            <div className="flex-1">
              <p className="font-medium">{officeSettingsSuccess}</p>
            </div>
          </div>
        )}

        {isLoadingOfficeSettings ? (
          <LoadingState message="Carregando configurações do escritório..." />
        ) : (
          <div className="space-y-4">
            {sections.map((section) => (
              <section key={section.title} className="rounded-2xl border border-background-200 bg-background-50 p-4">
                <div className="mb-4">
                  <h3 className="text-base font-semibold text-foreground-800">{section.title}</h3>
                  <p className="text-sm text-foreground-500 mt-0.5">{section.description}</p>
                </div>
                {section.content}
              </section>
            ))}

            <DocumentBrandingSettingsCard />
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default SettingsPage;
